import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, Area, AreaChart, PieChart, Pie, Cell
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Loader2, TrendingUp, TrendingDown, Minus, BarChart3, PieChart as PieIcon, Activity } from 'lucide-react';

const COLORS = ['#0066CC', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

const MESES_PT = {
  '01': 'Jan', '02': 'Fev', '03': 'Mar', '04': 'Abr',
  '05': 'Mai', '06': 'Jun', '07': 'Jul', '08': 'Ago',
  '09': 'Set', '10': 'Out', '11': 'Nov', '12': 'Dez'
};

export default function TendenciasChart() {
  const [loading, setLoading] = useState(true);
  const [tendencias, setTendencias] = useState([]);
  const [resumo, setResumo] = useState(null);
  const [distribuicao, setDistribuicao] = useState([]);
  const [activeTab, setActiveTab] = useState('tendencias');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };
      const apiUrl = process.env.REACT_APP_BACKEND_URL;

      // Load tendências
      const tendenciasRes = await fetch(`${apiUrl}/api/analytics/candidaturas/tendencias?meses=12`, { headers });
      if (tendenciasRes.ok) {
        const data = await tendenciasRes.json();
        // Format month labels
        const formatted = (data.tendencias || []).map(t => ({
          ...t,
          mesLabel: formatMonth(t.mes)
        }));
        setTendencias(formatted);
      }

      // Load resumo
      const resumoRes = await fetch(`${apiUrl}/api/analytics/dashboard/resumo`, { headers });
      if (resumoRes.ok) {
        setResumo(await resumoRes.json());
      }

      // Load distribuição por distrito
      const distRes = await fetch(`${apiUrl}/api/analytics/instituicoes/por-distrito`, { headers });
      if (distRes.ok) {
        const data = await distRes.json();
        setDistribuicao(data.distribuicao || []);
      }

    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatMonth = (mes) => {
    if (!mes) return '';
    const [year, month] = mes.split('-');
    return `${MESES_PT[month] || month} ${year.slice(2)}`;
  };

  const getTendenciaIcon = (valor) => {
    if (valor > 0) return <TrendingUp className="w-4 h-4 text-green-500" />;
    if (valor < 0) return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-slate-400" />;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#0066CC]" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      {resumo && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Instituições Ativas</CardDescription>
              <CardTitle className="text-2xl">{resumo.instituicoes?.ativas || 0}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xs text-slate-500">
                {resumo.instituicoes?.em_analise || 0} em análise
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Candidaturas Este Mês</CardDescription>
              <CardTitle className="text-2xl flex items-center gap-2">
                {resumo.candidaturas?.este_mes || 0}
                {getTendenciaIcon(resumo.candidaturas?.tendencia_percentual)}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xs text-slate-500">
                {resumo.candidaturas?.tendencia_percentual > 0 ? '+' : ''}
                {resumo.candidaturas?.tendencia_percentual || 0}% vs mês anterior
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Pendentes</CardDescription>
              <CardTitle className="text-2xl text-amber-600">{resumo.pendentes?.pedidos_alteracao || 0}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xs text-slate-500">
                {resumo.pendentes?.documentos || 0} docs aguardam validação
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Visitas Esta Semana</CardDescription>
              <CardTitle className="text-2xl">{resumo.visitas?.esta_semana || 0}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-xs text-slate-500">
                Agendadas
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Charts */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-[#0066CC]" />
                Analytics e Tendências
              </CardTitle>
              <CardDescription>Evolução das candidaturas e distribuição</CardDescription>
            </div>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="tendencias" className="flex items-center gap-1">
                  <BarChart3 className="w-4 h-4" />
                  <span className="hidden sm:inline">Tendências</span>
                </TabsTrigger>
                <TabsTrigger value="distribuicao" className="flex items-center gap-1">
                  <PieIcon className="w-4 h-4" />
                  <span className="hidden sm:inline">Distribuição</span>
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent>
          {activeTab === 'tendencias' && (
            <div className="space-y-6">
              {/* Area Chart - Candidaturas ao longo do tempo */}
              <div>
                <h4 className="text-sm font-medium mb-4">Pedidos de Alteração por Mês</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={tendencias}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="mesLabel" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px'
                      }}
                    />
                    <Legend />
                    <Area 
                      type="monotone" 
                      dataKey="pedidos_aprovados" 
                      name="Aprovados"
                      stackId="1"
                      stroke="#10B981" 
                      fill="#10B981"
                      fillOpacity={0.6}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="pedidos_pendentes" 
                      name="Pendentes"
                      stackId="1"
                      stroke="#F59E0B" 
                      fill="#F59E0B"
                      fillOpacity={0.6}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="pedidos_rejeitados" 
                      name="Rejeitados"
                      stackId="1"
                      stroke="#EF4444" 
                      fill="#EF4444"
                      fillOpacity={0.6}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Bar Chart - Novas Instituições */}
              <div>
                <h4 className="text-sm font-medium mb-4">Novas Instituições via Portal</h4>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={tendencias}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="mesLabel" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px'
                      }}
                    />
                    <Legend />
                    <Bar 
                      dataKey="novas_instituicoes" 
                      name="Novas Instituições"
                      fill="#0066CC" 
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar 
                      dataKey="instituicoes_ativas" 
                      name="Ativadas"
                      fill="#10B981" 
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {activeTab === 'distribuicao' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Pie Chart */}
              <div>
                <h4 className="text-sm font-medium mb-4">Por Distrito (Top 10)</h4>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={distribuicao.slice(0, 10)}
                      dataKey="total"
                      nameKey="distrito"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={({ distrito, total }) => `${distrito}: ${total}`}
                      labelLine={false}
                    >
                      {distribuicao.slice(0, 10).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Table */}
              <div>
                <h4 className="text-sm font-medium mb-4">Todas as Regiões</h4>
                <div className="max-h-[300px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="text-left py-2 px-3">Distrito</th>
                        <th className="text-right py-2 px-3">Total</th>
                        <th className="text-right py-2 px-3">Ativas</th>
                      </tr>
                    </thead>
                    <tbody>
                      {distribuicao.map((d) => (
                        <tr key={d.distrito_id || d.distrito} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-2 px-3">{d.distrito}</td>
                          <td className="text-right py-2 px-3 font-medium">{d.total}</td>
                          <td className="text-right py-2 px-3">
                            <Badge variant="outline" className="bg-green-50 text-green-700">
                              {d.ativas}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
